import { useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import {
  getSlackIntegration,
  saveSlackIntegration,
  deleteSlackIntegration,
} from '../services/slack';
import type { SlackIntegration as SlackIntegrationData } from '../services/slack';
import { friendlyError } from '../utils/errors';
import { trackEvent } from '../firebase';
import { SectionCard } from './ui/SectionCard';
import { Skeleton } from './ui/Skeleton';
import { ErrorAlert } from './ui/ErrorAlert';
import { HelpDrawer } from './ui/HelpDrawer';

function SlackHelp() {
  return (
    <div>
      <h3 className="text-sm font-bold text-gray-800 mb-1">Set up Slack notifications</h3>
      <p className="text-sm text-gray-600 mb-4">
        Get a Slack message every time someone joins your waitlist. You&apos;ll need to create a
        Slack app and generate an Incoming Webhook URL — takes about 2 minutes.
      </p>
      <ol className="text-sm text-gray-600 space-y-3 pl-4 list-decimal mb-5">
        <li>
          Go to{' '}
          <a
            href="https://api.slack.com/apps"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-500 font-medium hover:underline"
          >
            api.slack.com/apps
          </a>{' '}
          and click <strong>Create New App</strong> → <strong>From scratch</strong>.
        </li>
        <li>
          Under <strong>Add features and functionality</strong>, choose{' '}
          <strong>Incoming Webhooks</strong> and toggle it on.
        </li>
        <li>
          Click <strong>Add New Webhook to Workspace</strong>, pick a channel, then click{' '}
          <strong>Allow</strong>.
        </li>
        <li>Copy the webhook URL that appears and paste it into the field below.</li>
      </ol>
      <a
        href="https://api.slack.com/apps"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs font-semibold text-brand-500 hover:underline"
      >
        Open Slack API →
      </a>

      <hr className="my-5 border-gray-200" />

      <h3 className="text-sm font-bold text-gray-800 mb-1">What you&apos;ll receive</h3>
      <p className="text-sm text-gray-600">
        A Block Kit message in your chosen channel showing the subscriber&apos;s email, the project
        name, and a link to your dashboard. Delivered within seconds of each sign-up.
      </p>
    </div>
  );
}

interface SlackIntegrationProps {
  user: User;
  projectId: string;
}

export function SlackIntegration({ user, projectId }: SlackIntegrationProps) {
  const [integration, setIntegration] = useState<SlackIntegrationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [urlInput, setUrlInput] = useState('');
  const [labelInput, setLabelInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // null = undetermined (loading), resolves to true when integration exists
  const [collapsed, setCollapsed] = useState<boolean | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getSlackIntegration(user, projectId)
      .then((data) => {
        setIntegration(data);
        setCollapsed(data !== null);
      })
      .catch((err: unknown) => {
        setError(friendlyError(err));
        setCollapsed(false);
      })
      .finally(() => setLoading(false));
  }, [user, projectId]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveError(null);
    if (!urlInput.trim()) {
      setSaveError('Please enter your Slack webhook URL.');
      return;
    }
    setSaving(true);
    try {
      const saved = await saveSlackIntegration(user, projectId, urlInput.trim(), labelInput.trim());
      trackEvent('connect_slack', { project_id: projectId });
      setIntegration(saved);
      setUrlInput('');
      setLabelInput('');
    } catch (err: unknown) {
      setSaveError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm('Disconnect Slack? New subscribers will no longer trigger Slack notifications.'))
      return;
    setDisconnecting(true);
    try {
      await deleteSlackIntegration(user, projectId);
      trackEvent('disconnect_slack', { project_id: projectId });
      setIntegration(null);
      setCollapsed(false);
    } catch (err: unknown) {
      setError(friendlyError(err));
    } finally {
      setDisconnecting(false);
    }
  }

  const isCollapsed = collapsed === true;
  const webhookPreview = integration
    ? integration.webhookUrl.length > 42
      ? integration.webhookUrl.slice(0, 42) + '…'
      : integration.webhookUrl
    : '';

  const headerActions = (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => {
          setHelpOpen(true);
          trackEvent('open_slack_help', { project_id: projectId });
        }}
        className="bg-transparent border border-gray-300 cursor-pointer text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-brand-500 hover:border-brand-400 transition-colors"
        aria-label="Open Slack setup help"
      >
        ?
      </button>
      {collapsed !== null && (
        <button
          onClick={() => setCollapsed(!isCollapsed)}
          className="bg-transparent border-0 cursor-pointer text-xl px-2 text-gray-500 hover:text-gray-700"
          aria-label={isCollapsed ? 'Expand Slack integration' : 'Collapse Slack integration'}
          type="button"
        >
          {isCollapsed ? '▶' : '▼'}
        </button>
      )}
    </div>
  );

  const titleNode = (
    <div className="flex items-center gap-3">
      <span>💬 Slack integration</span>
      {isCollapsed && integration && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
            ✓ Connected
          </span>
          {integration.label ? (
            <span className="text-xs text-gray-400 hidden sm:inline">{integration.label}</span>
          ) : (
            <span className="text-xs text-gray-400 hidden sm:inline">{webhookPreview}</span>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <SectionCard title={titleNode} actions={headerActions}>
        {!isCollapsed && collapsed !== null && (
          <>
            {loading && (
              <div className="space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-5 w-1/2" />
              </div>
            )}

            {!loading && error && (
              <ErrorAlert label="Couldn't load Slack integration" message={error} />
            )}

            {!loading && !error && integration && (
              /* ── Connected state ── */
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                    ✓ Connected
                  </span>
                  {integration.label && (
                    <span className="text-sm text-gray-600">{integration.label}</span>
                  )}
                </div>
                <div className="mb-1">
                  <p className="text-sm font-medium text-gray-700 mb-0.5">Webhook URL</p>
                  <code className="block text-xs bg-gray-100 px-1.5 py-0.5 rounded break-all">
                    {integration.webhookUrl}
                  </code>
                </div>
                {integration.connectedAt && (
                  <p className="text-xs text-gray-400 mb-4">
                    Connected {new Date(integration.connectedAt).toLocaleDateString()}
                  </p>
                )}
                <button
                  className="px-3 py-1.5 text-sm font-semibold border border-red-400 rounded-md bg-transparent text-red-500 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  type="button"
                >
                  {disconnecting ? 'Disconnecting…' : 'Disconnect Slack'}
                </button>
              </div>
            )}

            {!loading && !error && !integration && (
              /* ── Unconnected state ── */
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Get a Slack message every time someone joins. Hit <strong>?</strong> for setup
                  instructions.
                </p>

                {/* Connect form */}
                <form onSubmit={handleSave} className="space-y-3">
                  <div>
                    <label
                      htmlFor="slack-url"
                      className="block text-xs font-semibold text-gray-700 mb-1"
                    >
                      Webhook URL
                    </label>
                    <input
                      id="slack-url"
                      type="url"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:border-brand-500 transition-colors"
                      placeholder="https://hooks.slack.com/services/…"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      disabled={saving}
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="slack-label"
                      className="block text-xs font-semibold text-gray-700 mb-1"
                    >
                      Label <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      id="slack-label"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:border-brand-500 transition-colors"
                      placeholder="e.g. #general on My Workspace"
                      value={labelInput}
                      onChange={(e) => setLabelInput(e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  {saveError && <p className="text-red-500 text-sm">{saveError}</p>}
                  <button
                    type="submit"
                    className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white border-0 rounded-md text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={saving}
                  >
                    {saving ? 'Connecting…' : 'Connect Slack'}
                  </button>
                </form>
              </>
            )}
          </>
        )}
      </SectionCard>
      <HelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Set up Slack notifications"
      >
        <SlackHelp />
      </HelpDrawer>
    </>
  );
}
